import { useState, useCallback, useEffect } from "react";
import { signUpUser, confirmSignUp, resendSignUpCode, formatAuthError } from "../services/cognito";
import kiraMinimalist from "../../../assets/Kira-minimalist.png";

export type RegisterStep = "SIGNUP" | "CONFIRM_SIGNUP";

interface UseRegisterReturn {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  code: string;
  error: string;
  successMessage: string;
  isLoading: boolean;
  step: RegisterStep;
  setName: (value: string) => void;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setCode: (value: string) => void;
  setError: (value: string) => void;
  setSuccessMessage: (value: string) => void;
  setStep: (step: RegisterStep) => void;
  handleSignUp: (e: React.FormEvent) => Promise<void>;
  handleConfirmSignUp: (e: React.FormEvent, onSuccess: () => void) => Promise<void>;
  handleResendCode: () => Promise<void>;
  resetForm: () => void;
}

export function useRegister(): UseRegisterReturn {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<RegisterStep>("SIGNUP");

  const resetForm = useCallback(() => {
    setName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setCode("");
    setError("");
    setSuccessMessage("");
    setStep("SIGNUP");
    setIsLoading(false);
  }, []);

  const handleSignUp = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!name.trim() || !email.trim() || !password.trim()) {
      setError("Por favor, completá todos los campos obligatorios.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    const isMinLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[^a-zA-Z0-9]/.test(password);

    if (!isMinLength || !hasUpperCase || !hasNumber || !hasSymbol) {
      setError("La contraseña no cumple con todos los requisitos de seguridad (mínimo 8 caracteres, mayúscula, número y símbolo).");
      return;
    }

    setIsLoading(true);
    try {
      // Set the username equal to email as per requirement, and pass name and minimalist picture
      await signUpUser(
        email.trim(),
        email.trim(),
        password,
        name.trim(),
        kiraMinimalist
      );
      setStep("CONFIRM_SIGNUP");
    } catch (err: any) {
      setError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  }, [name, email, password, confirmPassword]);

  const handleConfirmSignUp = useCallback(async (e: React.FormEvent, onSuccess: () => void) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!code.trim()) {
      setError("Por favor, ingresá el código de verificación.");
      return;
    }

    setIsLoading(true);
    try {
      await confirmSignUp(email.trim(), code.trim());
      setSuccessMessage("Registro confirmado con éxito. Serás redirigido al login en unos segundos...");
      setTimeout(() => {
        onSuccess();
        resetForm();
      }, 3000);
    } catch (err: any) {
      setError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  }, [email, code, resetForm]);

  const handleResendCode = useCallback(async () => {
    setError("");
    setSuccessMessage("");
    try {
      await resendSignUpCode(email.trim());
      setSuccessMessage("Código de verificación reenviado con éxito.");
    } catch (err: any) {
      setError(formatAuthError(err));
    }
  }, [email]);

  useEffect(() => {
    if (step === "CONFIRM_SIGNUP") {
      try {
        window.history.pushState({ authStep: "CONFIRM_SIGNUP" }, "");
      } catch (e) {
        // ignore history error if blocked
      }
    }
  }, [step]);

  useEffect(() => {
    const handlePopState = () => {
      if (step === "CONFIRM_SIGNUP") {
        resetForm();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [step, resetForm]);

  return {
    name,
    email,
    password,
    confirmPassword,
    code,
    error,
    successMessage,
    isLoading,
    step,
    setName,
    setEmail,
    setPassword,
    setConfirmPassword,
    setCode,
    setError,
    setSuccessMessage,
    setStep,
    handleSignUp,
    handleConfirmSignUp,
    handleResendCode,
    resetForm,
  };
}
